/** @format */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion"; // Added AnimatePresence
import { Droplet, Info, Ruler } from "lucide-react"; // Changed icon
import Customize_Reworked from "./index"; // Import the layout wrapper
import { customizeProduct, getBottomBars } from "../../actions/blindActions";
import { description, title } from "../../constants/tooltipConstants"; // Assuming these exist and are correct
import ImageZoomWithButton from "../../components/button/ImageZoomWithButton";
import { Tooltip } from "./Tooltip"; // Assuming Tooltip component exists
import { Link } from "react-router-dom";

// Helper function to get display name (keep as is or refine if needed)
function getHemTypeDisplayName(hemType) {
	if (!hemType) return "";
	// Simplified mapping
	const nameMap = {
		interlude: "Banded Bar",
		"interlude 2.0": "Aluminum Bar (Interlude)", // Distinguish if necessary
		"accubar 2.1": "Aluminum Hem",
		illusion: "Aluminum Bar (Illusion)", // Distinguish if necessary
	};
	return nameMap[hemType.toLowerCase()] || hemType; // Return mapped name or original
}

const Customize_BottomBar = () => {
	/*===============================  VARIABLES  =================================*/
	const dispatch = useDispatch();
	const { productInfo = {}, optionInfo = {} } = useSelector(
		(state) => state.customize?.customizeProduct || {}
	);

	// --- Component State ---
	// Initialize state from Redux store or defaults
	const [bottomBar, setBottomBar] = useState(productInfo?.Bottom_Bar || "");
	const [bottomBarColor, setBottomBarColor] = useState(
		productInfo?.Bottom_Bar_Color || "-"
	);
	const [selectedWrapType, setSelectedWrapType] = useState(
		productInfo?.Hem_Wrap_Type || "No"
	);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null); // Add error state
	const [data, setData] = useState([]); // Holds the fetched bottom bar options

	const shadeTitle = (
		productInfo?.Type ? `${productInfo.Type} Shade` : ""
	).toLowerCase();

	/*===============================  USE EFFECT  ================================*/

	// Load bottom bar options on initial mount or when shadeTitle changes
	useEffect(() => {
		if (shadeTitle) {
			loadBottomBars();
		} else {
			// Handle case where product type isn't set yet
			setError("Product type not available. Please go back to Details.");
			setLoading(false);
		}
	}, [shadeTitle]); // Re-fetch if the shade type changes (unlikely in current flow, but good practice)

	// Dispatch updates to Redux when selections change
	useEffect(() => {
		// Don't dispatch while loading or if there's an error
		if (loading || error) return;
		dispatchDetails();
	}, [bottomBar, bottomBarColor, selectedWrapType, loading, error]); // Include loading/error to prevent dispatching stale data

	/*===============================  FUNCTIONS  =================================*/

	// Fetch bottom bar options from API
	const loadBottomBars = async () => {
		setLoading(true);
		setError(null); // Reset error on new load attempt
		setData([]); // Clear previous data

		try {
			// Use defaultBottomBar from productInfo if available, otherwise may need a fallback
			const res = await getBottomBars(
				shadeTitle,
				productInfo.defaultBottomBar || "Accubar 2.1" // Provide a sensible fallback if default is missing
			);

			console.log("Fetched Bottom Bars:", res);

			// Filter out items explicitly marked as "Wrapped" in hem_type if that's the desired logic
			const filteredData = res.filter(
				(item) => !item.hem_type?.toLowerCase().includes("wrapped")
			);

			if (filteredData.length === 0) {
				setError("No compatible bottom bars found for this configuration.");
				setLoading(false);
				return;
			}

			setData(filteredData);

			// --- Post-load State Correction ---
			// 1. Check if the current `bottomBar` selection is valid among fetched options
			const currentSelectionValid = filteredData.some(
				(item) => item.hem_type === bottomBar
			);

			let correctedBottomBar = bottomBar;
			let correctedColor = bottomBarColor;
			let correctedWrap = selectedWrapType;

			if (!currentSelectionValid || !bottomBar) {
				// If current selection is invalid or empty, default to the first available option
				correctedBottomBar = filteredData[0].hem_type;
				console.log("Correcting Bottom Bar selection to:", correctedBottomBar);
			}

			// 2. Get the data for the (potentially corrected) bottom bar
			const selectedBarData = filteredData.find(
				(item) => item.hem_type === correctedBottomBar
			);

			if (selectedBarData) {
				// 3. Check if the current `bottomBarColor` is valid for the selected bar
				const availableColors = selectedBarData.colour || [];
				if (correctedBottomBar === "Plain Hem") {
					correctedColor = "Standard"; // Force color for Plain Hem
				} else if (
					!availableColors.includes(correctedColor) ||
					correctedColor === "-"
				) {
					// If color is invalid or '-', default to the first available color
					correctedColor = availableColors[0] || "-"; // Default to '-' if no colors
					console.log("Correcting Bottom Bar Color to:", correctedColor);
				}

				// 4. Correct wrap type if 'Yes' is selected but bar doesn't support it (e.g., not Accubar)
				if (
					correctedWrap === "Yes" &&
					!correctedBottomBar.toLowerCase().includes("accubar")
				) {
					correctedWrap = "No";
					console.log(
						"Correcting Wrap Type to 'No' as selected bar doesn't support wrapping."
					);
				}
			} else {
				// This case should ideally not happen if filteredData is not empty, but good to handle
				console.error(
					"Could not find data for the selected bottom bar:",
					correctedBottomBar
				);
				correctedColor = "-";
				correctedWrap = "No";
			}

			// Update state only if corrections were needed
			if (correctedBottomBar !== bottomBar) setBottomBar(correctedBottomBar);
			if (correctedColor !== bottomBarColor) setBottomBarColor(correctedColor);
			if (correctedWrap !== selectedWrapType)
				setSelectedWrapType(correctedWrap);

			setLoading(false);
		} catch (error) {
			console.error("Error loading bottom bars:", error);
			setError(
				"Failed to load bottom bar options. Please try again or contact support."
			);
			setLoading(false);
		}
	};

	// Dispatch current selections to Redux store
	const dispatchDetails = () => {
		// Ensure productInfo exists
		if (!productInfo) return;
		dispatch(
			customizeProduct({
				productInfo: {
					...productInfo,
					// Use the potentially corrected state values
					Bottom_Bar: bottomBar,
					Bottom_Bar_Color: bottomBarColor,
					Hem_Wrap_Type: selectedWrapType,
					error: !!error, // Set error flag based on component error state
				},
				optionInfo: {
					...optionInfo,
				},
			})
		);
	};

	// Check if a given bottom bar type is currently selected
	const isSelected = (itemHemType) => {
		if (!itemHemType || !bottomBar) return false;
		return itemHemType.toLowerCase() === bottomBar.toLowerCase();
	};

	// Handle selecting a different bottom bar type
	const handleBottomBarSelect = (selectedHemType) => {
		setBottomBar(selectedHemType);

		// Find the data for the newly selected type
		const selectedData = data.find((item) => item.hem_type === selectedHemType);
		if (!selectedData) return; // Should not happen if data is loaded

		// Reset color and wrap type based on the new selection
		if (selectedHemType === "Plain Hem") {
			setBottomBarColor("Standard");
			setSelectedWrapType("No"); // Plain hem cannot be wrapped
		} else {
			// Get available colors for the new selection
			const availableColors = selectedData.colour || [];
			// If current color is not valid for new bar, default to first color
			if (!availableColors.includes(bottomBarColor)) {
				setBottomBarColor(availableColors[0] || "-");
			}
			// Reset wrap type if the new bar doesn't support it
			if (
				selectedWrapType === "Yes" &&
				!selectedHemType.toLowerCase().includes("accubar")
			) {
				setSelectedWrapType("No");
			}
		}
	};

	// Handle changing the color for the currently selected bottom bar
	const handleBottomBarColorChange = (event) => {
		setBottomBarColor(event.target.value);
	};

	// Handle changing the wrap type (only applicable for Accubar)
	const handleWrapTypeChange = (event) => {
		const newWrapType = event.target.value;
		setSelectedWrapType(newWrapType);
		// Note: The actual `Bottom_Bar` value in Redux might need updating
		// based on wrap type elsewhere (e.g., in summary or cart logic)
		// Or, modify `bottomBar` state here if `dispatchDetails` relies on it
		// Example (if needed):
		// if (bottomBar.toLowerCase().includes('accubar')) {
		//     const baseName = "Accubar 2.1"; // Assuming this is the base name
		//     setBottomBar(newWrapType === 'Yes' ? `Front Wrapped ${baseName}` : baseName);
		// }
	};

	/*===============================  RENDER  ====================================*/
	return (
		// Wrap step content in the layout component
		<Customize_Reworked>
			{/* Main content div for this step */}
			<div className="space-y-8">
				{/* --- Header --- */}
				<div className="flex items-center justify-between pb-2 border-b border-gray-200">
					<div className="flex items-center gap-2">
						<div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
							<Droplet className="w-5 h-5 text-primary" /> {/* Changed Icon */}
						</div>
						<h3 className="text-lg font-medium">Select Bottom Bar</h3>
					</div>
					{/* Optional Help Link */}
					{/* <Link to="/guides/bottom-bars" className="text-primary text-xs hover:underline ml-auto inline-flex items-center gap-1" target="_blank" rel="noopener noreferrer">
                         <HelpCircle size={14}/> Bottom Bar Info
                    </Link> */}
				</div>

				{/* --- Conditional Rendering: Loading, Error, Options --- */}
				<AnimatePresence mode="wait">
					{loading ? (
						<motion.div
							key="loading"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="flex flex-col items-center justify-center h-40 text-gray-500"
						>
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-3"></div>
							<p>Loading bottom bar options...</p>
						</motion.div>
					) : error ? (
						<motion.div
							key="error"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							className="flex flex-col items-center justify-center h-40 text-red-600 bg-red-50 p-6 rounded-lg border border-red-200"
						>
							<p className="font-medium text-center">{error}</p>
							<Link
								to="/customize/details"
								className="mt-4 text-sm text-primary hover:underline"
							>
								Go back to Details
							</Link>
						</motion.div>
					) : (
						// --- Options Grid ---
						<motion.div
							key="options"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
						>
							{data.map((item) => {
								const currentSelection = isSelected(item.hem_type);
								const itemDisplayName = getHemTypeDisplayName(item.hem_type);
								const supportsWrapping = item.hem_type
									?.toLowerCase()
									.includes("accubar");
								const tooltipTitle = title[item.hem_type] || itemDisplayName; // Fallback tooltip title
								const tooltipDescription =
									description[item.hem_type] ||
									"Details about this bottom bar."; // Fallback tooltip description

								return (
									<motion.label // Use label for better accessibility with radio button
										key={item.hem_type}
										htmlFor={`bb-${item.hem_type}`}
										whileHover={{
											y: -3,
											boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
										}}
										transition={{ duration: 0.15 }}
										className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all flex flex-col ${
											currentSelection
												? "border-primary bg-primary/5 shadow-md"
												: "border-gray-200 hover:border-gray-300 bg-white"
										}`}
									>
										{/* Image Section */}
										<div className="rounded-lg overflow-hidden mb-3 border border-gray-100 bg-gray-50">
											<ImageZoomWithButton
												item={{
													image: item.hem_image,
													colour: itemDisplayName,
												}}
												isFabricSelected={currentSelection}
												customize={true}
												hideButton={true} // Hide default button
											/>
										</div>

										{/* Content Section */}
										<div className="space-y-3 mt-auto pt-2">
											{/* Title, Radio, Tooltip */}
											<div className="flex items-start justify-between gap-2">
												<div className="flex items-center gap-2 flex-grow">
													<input
														id={`bb-${item.hem_type}`}
														aria-label={itemDisplayName}
														type="radio"
														name="bottomBar" // Group radios
														value={item.hem_type}
														checked={currentSelection}
														onChange={() =>
															handleBottomBarSelect(item.hem_type)
														}
														className="w-4 h-4 mt-0.5 text-primary focus:ring-primary/50 focus:ring-offset-1 flex-shrink-0"
													/>
													<span className="font-medium text-sm">
														{itemDisplayName}
													</span>
												</div>
												{tooltipDescription && (
													<Tooltip
														title={tooltipTitle}
														content={tooltipDescription}
													>
														<button
															type="button" // Prevent form submission if inside form
															className="w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:bg-gray-100 flex-shrink-0"
														>
															<Info size={12} />
														</button>
													</Tooltip>
												)}
											</div>

											{/* Color Selection (Conditional) */}
											{item.hem_type !== "Plain Hem" && (
												<div className="space-y-1.5">
													<label
														htmlFor={`bb-color-${item.hem_type}`}
														className="block text-xs font-medium text-gray-600"
													>
														Bar Colour
													</label>
													<select
														id={`bb-color-${item.hem_type}`}
														aria-label="Bar Colour"
														value={currentSelection ? bottomBarColor : ""}
														onChange={handleBottomBarColorChange}
														// Disable if this card is not selected
														disabled={!currentSelection}
														onClick={(e) => e.stopPropagation()} // Prevent card click when interacting with select
														className={`w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-0 focus:border-primary outline-none appearance-none transition-opacity duration-150 ${
															!currentSelection
																? "opacity-50 cursor-not-allowed"
																: ""
														}`}
													>
														{/* <option value="" disabled={currentSelection}>--Select--</option> */}
														{(item.colour || []).map((color) => (
															<option key={color} value={color}>
																{color}
															</option>
														))}
														{/* Add fallback if no colors */}
														{(item.colour || []).length === 0 && (
															<option value="-">-</option>
														)}
													</select>
												</div>
											)}

											{/* Wrap Type Selection (Conditional) */}
											{supportsWrapping && (
												<div className="space-y-1.5">
													<label
														htmlFor={`bb-wrap-${item.hem_type}`}
														className="block text-xs font-medium text-gray-600"
													>
														Fabric Wrapped
													</label>
													<select
														id={`bb-wrap-${item.hem_type}`}
														aria-label="Fabric Wrapped"
														value={currentSelection ? selectedWrapType : "No"}
														onChange={handleWrapTypeChange}
														disabled={!currentSelection}
														onClick={(e) => e.stopPropagation()} // Prevent card click
														className={`w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:ring-0 focus:border-primary outline-none appearance-none transition-opacity duration-150 ${
															!currentSelection
																? "opacity-50 cursor-not-allowed"
																: ""
														}`}
													>
														<option value="No">No</option>
														<option value="Yes">Yes</option>
													</select>
												</div>
											)}
										</div>
									</motion.label>
								);
							})}
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</Customize_Reworked>
	);
};

export default Customize_BottomBar;
